import React, { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Menu from '@groovehq/internal-design-system/lib/components/Dropdown/DropdownMenu'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import {
  divider,
  text,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { Exit } from '@groovehq/internal-design-system/lib/assets/icons'
import { selectAccountPreferenceDebugFrontend } from 'selectors/app/selectAccountPreferences'
import {
  selectCurrentUser,
  selectCurrentUserCanAccessAdmin,
} from 'ducks/currentUser/selectors'
import { selectFlag } from 'ducks/flags/selectors'
import { doSetFlag } from 'ducks/flags/operations'
import { HAS_SEEN_INSTALL_APP_MENU } from 'ducks/flags/flagNames'
import DebugButton from 'components/DebugButton'
import debug from 'util/debug'
import LegacyLink from 'components/LegacyLink'
import Avatar from 'components/Avatar'
import AppsMenuContainer from 'components/AppsMenu/AppsMenuContainer'
import { LOGOUT_PAGE } from 'constants/pages'
import {
  SETTINGS_PROFILE_PAGE,
  SETTINGS_NOTIFICATIONS_PAGE,
  SETTINGS_PREFERENCES_PAGE,
} from 'subapps/settings/types'
import Link from 'redux-first-router-link'
import { styles } from './styles'

const TO_SETTINGS_PROFILE_PAGE = {
  type: SETTINGS_PROFILE_PAGE,
}

const TO_SETTINGS_NOTIFICATIONS_PAGE = {
  type: SETTINGS_NOTIFICATIONS_PAGE,
}

const TO_SETTINGS_PREFERENCES_PAGE = {
  type: SETTINGS_PREFERENCES_PAGE,
}

const menuOffset = [-3, 9]

const FullMenu = React.memo(({ onOpenAppsMenu }) => {
  const dispatch = useDispatch()
  const canDebugFrontend = useSelector(selectAccountPreferenceDebugFrontend)
  const isDebug = canDebugFrontend || debug.enabled
  const isSuperAdmin = useSelector(selectCurrentUserCanAccessAdmin)

  const handleLogout = useCallback(
    () => {
      dispatch({ type: LOGOUT_PAGE })
    },
    [dispatch]
  )

  return (
    <Menu as="div" css={styles.menuStyle}>
      <Menu.Item
        itemKey="inbox-header"
        as="div"
        header
        css={text.styles.textSm}
      >
        My settings
      </Menu.Item>
      <Link className="grui" to={TO_SETTINGS_PROFILE_PAGE}>
        <Menu.Item itemKey="my-profile" className="grui">
          My profile
        </Menu.Item>
      </Link>
      <Link className="grui" to={TO_SETTINGS_NOTIFICATIONS_PAGE}>
        <Menu.Item itemKey="notifications" className="grui">
          Notifications
        </Menu.Item>
      </Link>
      <Link className="grui" to={TO_SETTINGS_PREFERENCES_PAGE}>
        <Menu.Item itemKey="preferences" className="grui">
          Preferences
        </Menu.Item>
      </Link>
      {isSuperAdmin && (
        <>
          <LegacyLink
            path="/admin/accounts"
            className="grui"
            async={false}
            skipModal
          >
            <Menu.Item itemKey="go-to-admin" className="grui">
              Go to admin
            </Menu.Item>
          </LegacyLink>
          <LegacyLink path="/sidekiq" className="grui" async={false} skipModal>
            <Menu.Item itemKey="go-to-sidekiq" className="grui">
              Sidekiq Dashboard
            </Menu.Item>
          </LegacyLink>
        </>
      )}
      {isDebug && <DebugButton as={Menu.Item} />}
      <div css={divider.styles.primary} role="separator" />
      <Menu.Item
        as="div"
        itemKey="apps-menu"
        className="grui"
        onClick={onOpenAppsMenu}
      >
        <span className="grui -ml-2">
          <span css={styles.emoji}>📱</span>
          Get our mobile app
        </span>
      </Menu.Item>
      <Menu.Item
        itemKey="kb"
        as="a"
        href="https://help.groovehq.com/help"
        target="_blank"
        rel="noopener noreferrer"
        className="grui"
      >
        Groove Knowledge Base
      </Menu.Item>
      <Menu.Item
        itemKey="logout"
        as="button"
        type="button"
        className="grui"
        onClick={handleLogout}
      >
        <div className="grui flex items-center" css={styles.logout}>
          <Exit className="grui mt-2 mr-2" css={text.styles.textMediumDark} />
          Log out
        </div>
      </Menu.Item>
    </Menu>
  )
})

const SettingsMenu = React.memo(() => {
  const dispatch = useDispatch()
  const currentUser = useSelector(selectCurrentUser)
  const userRef = useRef()
  const [openAppsMenu, setOpenAppsMenu] = useState(false)
  const hasSeenInstallAppMenu = useSelector(state =>
    selectFlag(state)(HAS_SEEN_INSTALL_APP_MENU)
  )

  const handleOpenAppsMenu = useCallback(
    e => {
      // Prevent progagating the click event to the document's click event in AppsMenuContainer
      e.nativeEvent.stopImmediatePropagation()
      setOpenAppsMenu(true)
      if (!hasSeenInstallAppMenu) {
        dispatch(doSetFlag(HAS_SEEN_INSTALL_APP_MENU))
      }
    },
    [dispatch, hasSeenInstallAppMenu]
  )

  return (
    <>
      <Dropdown
        overlay={<FullMenu onOpenAppsMenu={handleOpenAppsMenu} />}
        position="right-end"
        strategy="fixed"
        offset={menuOffset}
        autoHeight
      >
        <div css={styles.avatarContainer} className="grui mt-5 mr-5 ml-5">
          <div ref={userRef}>
            <Avatar size={32} actor={currentUser} />
          </div>
        </div>
      </Dropdown>
      <AppsMenuContainer
        referenceElement={userRef.current}
        open={openAppsMenu}
        setOpen={setOpenAppsMenu}
      />
    </>
  )
})

export default SettingsMenu
